import useQuery from "../../../../hooks/useQuery";
import { getDistinctValues, isEmpty, isFalse, toFixedValue } from "../../../../utils/helpers";
import { getCutOrderLabel, getGroupOrderLabel, getWhFlowInfoList } from "../../../../helpers/orderHelper";
import useSafeState from "../../../../hooks/useSafeState";

import useDeepEffect from "../../../../hooks/useDeepEffect";
import useMutation from "../../../../hooks/useMutation";

import { useDeepMemo, useSetting, useTime } from "../../../../hooks";
import SettingKeys from "../../../../utils/SettingKeys";
import { FlowInfoFields } from "../../../../utils/entityFields";
import { format } from "../../../../utils/dateUtils";
import useWarehouse from "../../../../hooks/useWarehouse";
import { LotType, UOM } from "../../../../constants";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const delivery = (flowInfo, utc_offset) =>
  utc_offset && !isEmpty(flowInfo.exFactoryDate) ? format(flowInfo.exFactoryDate, utc_offset, "dd MMM, yy") : null;

const keySeparator = "$@$";

const usePoView = props => {
  const orderId = props.match.params.id;
  const routeState = props.location?.state || {};

  const [version, setVersion] = useSafeState(0);
  const [selectedKey, setSelectedKey] = useSafeState(null);

  const { utc_offset } = useTime();
  const [mutate] = useMutation();

  const { allWhList, whList, whLoading } = useWarehouse();
  const whCodes = whList.map(e => e.code);

  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");
  const flowInfoValidation = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, "flowInfo");

  const { data: order = {} } = useQuery(orderId && { url: `orders/${orderId}`, params: { page: "wh-quality" }, version });
  const { data: ledgerData = [] } = useQuery(orderId && { url: `ledgers`, params: { search: `order.id==${orderId}`, size: 1000 }, version });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    const whFlowInfoList = getWhFlowInfoList(order);
    const rowList = whFlowInfoList
      .filter(fi => isEmpty(fi.parentId))
      .flatMap(parent => {
        const children = whFlowInfoList.filter(fi => parent.flowInfoIds.includes(fi.parentId));
        if (isEmpty(children)) {
          return parent.partList
            .sort((a, b) => a.serial - b.serial)
            .map(part => ({
              ...parent,
              key: `${part.effPartName}${keySeparator}${parent.flowInfoIds.join(",")}`,
              effPartName: part.effPartName,
              ocu: part?.uom === UOM.GRAM.value ? (part?.ocu || 0) / 1000 : part?.ocu,
              uom: part?.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part?.uom,
              exFactoryDate: delivery(parent, utc_offset)
            }));
        } else {
          return [
            { ...parent, key: `${keySeparator}${parent.flowInfoIds.join(",")}`, exFactoryDate: delivery(parent, utc_offset) },
            ...children.flatMap(child =>
              child.partList
                .sort((a, b) => a.serial - b.serial)
                .map(part => ({
                  ...child,
                  key: `${part.effPartName}${keySeparator}${child.flowInfoIds.join(",")}`,
                  parentKey: `${keySeparator}${parent.flowInfoIds.join(",")}`,
                  effPartName: part.effPartName,
                  ocu: part?.uom === UOM.GRAM.value ? (part?.ocu || 0) / 1000 : part?.ocu,
                  uom: part?.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part?.uom,
                  exFactoryDate: delivery(child, utc_offset)
                }))
            )
          ];
        }
      })
      .map(row => ({
        ...row,
        poRef: order?.poRef,
        customerName: order?.customer?.name,
        seasonName: order?.season?.name,
        productName: row.product?.name
      }));

    return rowList;
  }, [order?.flowInfoList, utc_offset]);

  const selectedRow = flowInfoRows.find(e => e.key === selectedKey);

  useDeepEffect(() => {
    if (isEmpty(flowInfoRows)) return;
    // if (!isEmpty(routeState?.flowInfoId) && !isEmpty(routeState?.partId)) {
    //   setSelectedKey(`${routeState.flowInfoId}-${routeState.partId}`);
    // } else {
    //   setSelectedKey(flowInfoRows[0]?.key);
    // }
    const key = flowInfoRows.filter(f => !f.isParent)[0]?.key;
    setSelectedKey(key);
  }, [flowInfoRows, routeState]);

  // Columns and rows for Order flow table
  const flowInfoColumns = useDeepMemo(() => {
    if (isEmpty(flowInfoFields) || isEmpty(flowInfoValidation)) return [];

    let columns = [
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      {
        dataKey: FlowInfoFields.PRODUCT_NAME,
        title: "Product",
        align: "left",
        render: value => value || "-",
        filterOptions: { filter: true },
        highlight: true
      },
      { dataKey: FlowInfoFields.RATIO, title: "Ratio", render: value => value || "-", highlight: true },
      { dataKey: FlowInfoFields.STYLE, title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: FlowInfoFields.COLOR, title: "Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", render: value => value || "-", filterOptions: { filter: true }, highlight: true },
      { dataKey: FlowInfoFields.ORDER_QTY, title: "Order Qty", align: "right" },
      { dataKey: FlowInfoFields.EXTRA, title: "Extra Ship (%)", align: "right", render: value => (isEmpty(value) ? "-" : `${value}%`) },
      {
        dataKey: "ocu",
        title: "BOM CU*",
        align: "right",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} ${UomCodeFromValue[row.uom]}`)
      },
      {
        dataKey: "fabricRequired",
        title: "Required Fabric",
        align: "right",
        render: (value, _, row) =>
          !isEmpty(row?.orderQty) && !isEmpty(row?.ocu) ? `${toFixedValue(row.orderQty * row.ocu, 2)} ${UomCodeFromValue[row.uom]}` : "-"
      },
      {
        dataKey: "extraFabricRequired",
        title: "Required Fabric (Extra)",
        align: "right",
        render: (value, _, row) =>
          !isEmpty(row?.orderQty) && !isEmpty(row?.ocu)
            ? `${toFixedValue(row.orderQty * row.ocu * 0.01 * (100 + (row.extra || 0)))} ${UomCodeFromValue[row.uom]}`
            : "-"
      }
      // {
      //   dataKey: FlowInfoFields.STATE,
      //   title: "Status",
      //   length: 1000,
      //   render: (value, _, row) => renderFlowInfoStatus(row, onStateClick)
      // },
    ];

    if (flowInfoRows.every(e => isEmpty(e.ratio))) {
      columns = columns.filter(c => c.dataKey !== FlowInfoFields.RATIO);
    }

    return columns;
  }, [order, flowInfoFields, flowInfoValidation, flowInfoRows]);

  const { flowInfoIds, effPartName } = useDeepMemo(() => {
    const selectedRow = flowInfoRows.find(fi => fi.key === selectedKey);
    if (isEmpty(selectedRow)) return { flowInfoIds: [], effPartName: null };
    const [effPartName, flowInfoIds] = selectedRow.key?.split(keySeparator);
    return { flowInfoIds: flowInfoIds.split(",").map(id => Number(id)), effPartName };
  }, [flowInfoRows, selectedKey]);
  const flowInfoList = order?.flowInfoList?.filter(fi => flowInfoIds.includes(fi.id));

  const ledgerList = useDeepMemo(() => {
    if (isEmpty(flowInfoList) || whLoading) return [];
    const flowInfo = selectedRow || {};
    return ledgerData
      .filter(e => isEmpty(whCodes) || isEmpty(e.fabricItem?.warehouse) || whCodes.includes(e.fabricItem.warehouse))
      .filter(l => l.productId === flowInfo.product?.id && l.style === flowInfo.style && l.styleColor === flowInfo.color && l.part === effPartName);
  }, [flowInfoList, effPartName, ledgerData, whCodes, whLoading, selectedRow]);

  const fabricItemIds = getDistinctValues(ledgerList?.map(e => e.fabricItem?.id).filter(id => !isEmpty(id)));
  const fabricItems = !isEmpty(ledgerList)
    ? ledgerList.filter(e => !isEmpty(e.fabricItem)).reduce((acc, l) => ({ ...acc, [l.fabricItem.id]: l.fabricItem }), {})
    : {};

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: "rolls",
      params: { search: `fabricItem.id=in=(${fabricItemIds.join(",")});parentId=='NULL'`, size: 10000 },
      version
    }
  );
  const otherOrderIds = getDistinctValues(rollInfoList?.map(ri => ri.orderId).filter(id => !isEmpty(id) && id !== Number(orderId)));
  const { data: otherOrderList = [] } = useQuery(
    !isEmpty(otherOrderIds) && {
      url: `orders`,
      params: { search: `id=in=(${otherOrderIds.join(",")})`, deep: true }
    }
  );
  const orderFlowList = useDeepMemo(() => {
    if (isEmpty(order)) return [];
    return [order, ...(otherOrderList || [])].flatMap(order => order.orderFlowList.map(of => ({ ...of, order })));
  }, [order, otherOrderList]);

  const rollList = useDeepMemo(() => {
    if (isEmpty(rollInfoList) || isEmpty(fabricItemIds)) return [];
    const rollList = rollInfoList
      .filter(ri => fabricItemIds.includes(ri.fabricItemId))
      .map(ri => ({
        ...ri,
        fabricItem: fabricItems[ri.fabricItemId],
        orderFlow: orderFlowList?.find(of => of.id === ri.groupOrderId),
        cutOrderFlow: orderFlowList?.find(of => of.id === ri.cutOrderId)
      }))
      .map(ri => ({
        ...ri,
        groupOrder: getGroupOrderLabel(ri.orderFlow, ri.orderFlow?.order, ["poRef"]),
        cutOrder:
          ri.cutOrderFlow?.lotType === LotType.PRE_RUN
            ? getCutOrderLabel(ri.cutOrderFlow, ri.cutOrderFlow?.order, ["poRef", "lot", "inseam"])
            : getCutOrderLabel(ri.cutOrderFlow, ri.cutOrderFlow?.order, ["poRef", "inseam"])
      }));
    return rollList;
  }, [rollInfoList, fabricItemIds, orderFlowList, fabricItems]);

  const updateAssociation = (rows, _, onSubmitComplete) => {
    if (isEmpty(rows)) return;

    const options = {
      url: `ledgers`,
      method: "put",
      data: rows.map(row => ({ id: row.id, qty: row.associatedQty }))
    };

    mutate(options, {
      onSuccess: () => {
        setVersion(v => v + 1);
        onSubmitComplete && onSubmitComplete(rows);
      },
      onFailure: () => onSubmitComplete && onSubmitComplete(rows)
    });
  };

  const onRowClick = row => {
    if (isFalse(row.isParent)) {
      setSelectedKey(row.key);
    }
  };

  const onRefresh = () => {
    setVersion(v => v + 1);
  };

  return {
    order,
    selectedKey,
    flowInfoColumns,
    flowInfoRows,
    ledgerList,
    rollList,
    part: effPartName,
    flowInfoList,
    allWhList,
    whList,
    onRefresh,
    onRowClick,
    updateAssociation
  };
};

export default usePoView;
